import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Image,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { router, useFocusEffect } from "expo-router";
import { useDispatch, useSelector } from "react-redux";
import { Toast } from "react-native-toast-notifications";
import i18n from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import WebSidebar from "./WebSidebar";
import { RootState } from "../../store";
import { authActions } from "../../store/slices/authSlice";
import { appActions } from "../../store/slices/appSlice";

// Import store info image for header
import StoreInfoImage from "../../assets/settings/store-info.png";

const WebStoreInfoScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { user, refetch } = useSelector((state: RootState) => state.auth);
  const { locale } = useSelector((state: RootState) => state.app);

  const isRTL = locale === "ar" || i18n.locale === "ar";

  // State for fields
  const [storename, setStorename] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [categories, setCategories] = useState<
    Array<{ id_category: number; label: string }>
  >([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const userData = user as {
    storename?: string;
    description?: string;
    location?: string;
    categoryId?: number;
  } | null;

  useEffect(() => {
    if (userData) {
      setStorename(userData.storename || "");
      setDescription(userData.description || "");
      setLocation(userData.location || "");
      setCategoryId(userData.categoryId || null);
    }
  }, [userData]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const result = await customAxios.get("/marketplace/categories");
        setCategories(result.data.categories || []);
      } catch (error) {
        // silently ignore
      }
    };
    fetchCategories();
  }, []);

  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch]),
  );

  // Fetch user data on mount and when refetch changes
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const res = await customAxios.get("/users/check");
        dispatch(authActions.setUser(res.data.userinfo));
      } catch (error) {
        // silently ignore
      }
    };
    fetchUserData();
  }, [dispatch, refetch]);

  const handleGoBack = () => {
    router.push("/(drawer)/params" as any);
  };

  const getCategoryLabel = () => {
    if (categoryId && categories.length > 0) {
      const category = categories.find((cat) => cat.id_category === categoryId);
      return category?.label || i18n.t("selectCategory");
    }
    return i18n.t("selectCategory");
  };

  const handleCancelEdit = () => {
    if (userData) {
      setStorename(userData.storename || "");
      setDescription(userData.description || "");
      setLocation(userData.location || "");
      setCategoryId(userData.categoryId || null);
    }
    setIsEditing(false);
  };

  const handleSaveStoreInfo = async () => {
    setSaveLoading(true);
    try {
      const updatedFields: any = {};

      if (storename !== (userData?.storename || "")) {
        updatedFields.storename = storename;
      }
      if (description !== (userData?.description || "")) {
        updatedFields.description = description;
      }
      if (location !== (userData?.location || "")) {
        updatedFields.location = location;
      }
      if (categoryId !== (userData?.categoryId || null)) {
        updatedFields.categoryId = categoryId;
      }

      if (Object.keys(updatedFields).length === 0) {
        Toast.show(i18n.t("noChangesToSave"), { type: "info" });
        setSaveLoading(false);
        return;
      }

      await customAxios.patch(`/users/update`, updatedFields, {
        withCredentials: true,
      });

      Toast.show(i18n.t("profileUpdateSuccess"), { type: "success" });
      dispatch(authActions.toggleRefetch());
      setIsEditing(false);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      if (errorMessage) {
        Toast.show(errorMessage, { type: "danger" });
      } else {
        Toast.show(i18n.t("profileUpdateFailed"), { type: "danger" });
      }
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div
      key={`store-info-screen-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Header with breadcrumb and blue bar */}
          <View style={styles.headerSection}>
            <div
              className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
            >
              <View style={styles.headerIconContainer}>
                <Image
                  source={StoreInfoImage}
                  style={styles.headerIcon}
                  resizeMode="contain"
                />
              </View>
              <View style={styles.breadcrumb}>
                <TouchableOpacity onPress={handleGoBack}>
                  <Text style={styles.breadcrumbLink}>
                    {i18n.t("settings")}
                  </Text>
                </TouchableOpacity>
                <Text style={styles.breadcrumbSeparator}>{"  >  "}</Text>
                <Text style={styles.breadcrumbCurrent}>
                  {i18n.t("storeInfo")}
                </Text>
              </View>
            </div>
            <View style={styles.headerBar} />
          </View>

          {/* Edit/Save Button Bar */}
          <View style={styles.editButtonBar}>
            {!isEditing ? (
              <TouchableOpacity
                style={styles.editButton}
                onPress={() => setIsEditing(true)}
              >
                <Text style={styles.editButtonIcon}>✏️</Text>
                <Text style={styles.editButtonText}>
                  {i18n.t("changedata")}
                </Text>
              </TouchableOpacity>
            ) : (
              <View style={styles.editButtonRow}>
                <TouchableOpacity
                  style={styles.cancelButton}
                  onPress={handleCancelEdit}
                >
                  <Text style={styles.cancelButtonText}>
                    {i18n.t("cancel")}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.saveButton}
                  onPress={handleSaveStoreInfo}
                  disabled={saveLoading}
                >
                  {saveLoading ? (
                    <ActivityIndicator size="small" color="#FFF" />
                  ) : (
                    <>
                      <Text style={styles.saveButtonIcon}>💾</Text>
                      <Text style={styles.saveButtonText}>
                        {i18n.t("save")}
                      </Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Store Info Form */}
          <View style={styles.formContainer}>
            {/* Store Name Field */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>{i18n.t("storeName")}</Text>
              <View
                style={[
                  styles.fieldInputContainer,
                  isEditing && styles.fieldInputEditing,
                ]}
              >
                <Text style={styles.fieldIcon}>🏪</Text>
                {isEditing ? (
                  <TextInput
                    style={styles.fieldTextInput}
                    value={storename}
                    onChangeText={setStorename}
                    placeholder={i18n.t("defaultStoreName")}
                    placeholderTextColor="#9CA3AF"
                  />
                ) : (
                  <Text style={styles.fieldValue}>
                    {storename || i18n.t("defaultStoreName")}
                  </Text>
                )}
              </View>
            </View>

            {/* Store Description Field */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>
                {i18n.t("storeDescription")}
              </Text>
              <View
                style={[
                  styles.fieldInputContainer,
                  isEditing && styles.fieldInputEditing,
                  { minHeight: isEditing ? 100 : ("auto" as any) },
                ]}
              >
                <Text
                  style={[
                    styles.fieldIcon,
                    {
                      alignSelf: "flex-start",
                      marginTop: isEditing ? 4 : 0,
                    },
                  ]}
                >
                  📝
                </Text>
                {isEditing ? (
                  <TextInput
                    style={[
                      styles.fieldTextInput,
                      { minHeight: 80, textAlignVertical: "top" },
                    ]}
                    value={description}
                    onChangeText={setDescription}
                    placeholder={i18n.t("defaultStoreDescription")}
                    placeholderTextColor="#9CA3AF"
                    multiline={true}
                    numberOfLines={4}
                  />
                ) : (
                  <Text style={styles.fieldValue}>
                    {description || i18n.t("defaultStoreDescription")}
                  </Text>
                )}
              </View>
            </View>

            {/* Store Category Field with dropdown */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>{i18n.t("storeCategory")}</Text>
              <TouchableOpacity
                style={[
                  styles.fieldInputContainer,
                  isEditing && styles.fieldInputEditing,
                ]}
                onPress={() =>
                  isEditing && setShowCategoryDropdown(!showCategoryDropdown)
                }
                disabled={!isEditing}
              >
                {isEditing && (
                  <Text style={styles.dropdownArrow}>
                    {showCategoryDropdown ? "⌃" : "⌄"}
                  </Text>
                )}
                <View style={styles.fieldContentRight}>
                  <Text style={styles.fieldValue}>{getCategoryLabel()}</Text>
                  <Text style={styles.fieldIcon}>🏪</Text>
                </View>
              </TouchableOpacity>
              {showCategoryDropdown && isEditing && (
                <View style={styles.dropdownOptions}>
                  {categories.map((cat) => (
                    <TouchableOpacity
                      key={cat.id_category}
                      style={[
                        styles.dropdownOption,
                        categoryId === cat.id_category && styles.optionSelected,
                      ]}
                      onPress={() => {
                        setCategoryId(cat.id_category);
                        setShowCategoryDropdown(false);
                      }}
                    >
                      <Text style={styles.optionText}>{cat.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

            {/* Location Field */}
            <View style={styles.fieldContainer}>
              <Text style={styles.fieldLabel}>{i18n.t("location")}</Text>
              <View
                style={[
                  styles.fieldInputContainer,
                  isEditing && styles.fieldInputEditing,
                ]}
              >
                <Text style={styles.fieldIcon}>📍</Text>
                {isEditing ? (
                  <TextInput
                    style={styles.fieldTextInput}
                    value={location}
                    onChangeText={setLocation}
                    placeholder={i18n.t("defaultLocation") || "Location"}
                    placeholderTextColor="#9CA3AF"
                  />
                ) : (
                  <Text style={styles.fieldValue}>
                    {location || i18n.t("notSpecified")}
                  </Text>
                )}
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  mainContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    padding: 20,
  },
  headerSection: {
    marginBottom: 30,
  },
  breadcrumb: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbLink: {
    fontSize: 28,
    color: "#333",
  },
  breadcrumbSeparator: {
    fontSize: 28,
    color: "#999",
    marginHorizontal: 4,
  },
  breadcrumbCurrent: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#333",
  },
  headerIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E8F4F8",
    borderWidth: 4,
    borderColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  headerIcon: {
    width: 70,
    height: 70,
  },
  headerBar: {
    height: 8,
    backgroundColor: "#1379A2",
    borderRadius: 4,
    marginTop: 8,
  },
  editButtonBar: {
    alignItems: "center",
    marginBottom: 24,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#1379A2",
    borderRadius: 30,
    paddingVertical: 14,
    paddingHorizontal: 32,
    gap: 10,
  },
  editButtonIcon: {
    fontSize: 18,
  },
  editButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  editButtonRow: {
    flexDirection: "row",
    gap: 16,
  },
  cancelButton: {
    backgroundColor: "#6B7280",
    borderRadius: 30,
    paddingVertical: 14,
    paddingHorizontal: 32,
  },
  cancelButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#10B981",
    borderRadius: 30,
    paddingVertical: 14,
    paddingHorizontal: 32,
    gap: 10,
  },
  saveButtonIcon: {
    fontSize: 18,
  },
  saveButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  formContainer: {
    alignItems: "center",
    gap: 16,
    paddingHorizontal: 40,
  },
  fieldContainer: {
    width: "100%",
    maxWidth: 600,
  },
  fieldLabel: {
    fontSize: 16,
    color: "#666",
    textAlign: "right",
    marginBottom: 8,
  },
  fieldInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 30,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  fieldInputEditing: {
    borderColor: "#1379A2",
    borderWidth: 2,
  },
  fieldContentRight: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
  },
  fieldIcon: {
    fontSize: 22,
  },
  fieldValue: {
    flex: 1,
    fontSize: 18,
    color: "#333",
    textAlign: "right",
  },
  fieldTextInput: {
    flex: 1,
    fontSize: 18,
    color: "#333",
    textAlign: "right",
    paddingVertical: 0,
  },
  dropdownArrow: {
    fontSize: 20,
    color: "#666",
    marginRight: 12,
  },
  dropdownOptions: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    marginTop: 8,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  dropdownOption: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  optionSelected: {
    backgroundColor: "#E8F4F8",
  },
  optionText: {
    fontSize: 18,
    color: "#333",
    textAlign: "right",
  },
});

export default WebStoreInfoScreen;
